import { useQuery } from "@tanstack/react-query";

import { api } from "../api/rest";
import type { DecidedSignal, DecidedSource } from "../api/types";

/**
 * Такт опроса СВОДКИ.
 *
 * Реже, чем у карточек: сводка считается по часам наблюдения, и секунда
 * задержки в ней ничего не меняет.
 */
const DECIDED_REFETCH_MS = 15_000;

/**
 * Такт опроса ЛЕНТЫ — чаще сводки, и с вехи живого режима это не роскошь.
 *
 * Прежнее обоснование общего такта («кнопок у раздела нет, секунда задержки
 * ничего не стоит») перестало быть правдой: в ступени `alert` каждая строка
 * ленты несёт кнопку «взять», и жить ей ровно до конца окна. Строка рождается
 * за 17–46 с до него — то есть пятнадцатисекундный такт съедал бы до трети
 * всего времени на решение, а иногда и всё: строка, замеченная на такте после
 * конца окна, приезжает уже с «поздно».
 *
 * Три секунды, а не одна: ответ отдаёт до шестидесяти строк, а выигрыш от
 * ускорения дальше упирается в саму длину окна, не в такт.
 */
const DECIDED_FEED_REFETCH_MS = 3_000;

/**
 * Сводка почти решённого по двум источникам.
 *
 * `windowLen` — длительность окна серии в секундах (300, 900) либо 0 «все». Она
 * ЧАСТЬ КЛЮЧА запроса, а не только его параметр: срез меняет выборку, и кеш
 * прошлого среза выдал бы чужие числа под новым заголовком — тот же довод, что
 * у ревизии состава в семьях.
 */
export function useDecidedSummary(hours: number, windowLen = 0) {
  // fresh_book_ms приезжает с сервера, а не живёт константой здесь: подпись
  // «свежий стакан» обязана называть то же число, каким посчитаны колонки.
  return useQuery<{ hours: number; sources: DecidedSource[]; fresh_book_ms: number }>({
    queryKey: ["decided-summary", hours, windowLen],
    queryFn: () => api.decidedSummary(hours, windowLen),
    refetchInterval: DECIDED_REFETCH_MS,
    retry: 1,
  });
}

/** Лента входов, свежие сверху. В ступени `alert` в ней живут кнопки. */
export function useDecidedSignals(hours: number, limit = 60, windowLen = 0) {
  return useQuery<{ signals: DecidedSignal[] }>({
    queryKey: ["decided-signals", hours, limit, windowLen],
    queryFn: () => api.decidedSignals(hours, undefined, limit, windowLen),
    refetchInterval: DECIDED_FEED_REFETCH_MS,
    // В фоновой вкладке не опрашиваем: нажимать по строке всё равно некому, а
    // такт в три секунды на скрытой вкладке — это запрос каждые три секунды на
    // всю ночь.
    refetchIntervalInBackground: false,
    retry: 1,
  });
}
